import { Link } from "react-router-dom";
import { Zap, ArrowLeft } from "lucide-react";

const SECTIONS = [
  {
    title: "Data We Collect",
    items: [
      "Name — to personalise your account",
      "Email address — for account access, receipts and service updates",
      "Health and fitness data — including heart rate streamed from your Apple Watch during sessions",
      "Workout activity — sessions, durations, streaks and performance metrics",
    ],
  },
  {
    title: "How We Use Your Data",
    items: [
      "To provide the fit50 service — building and delivering your workouts and tracking progress",
      "To personalise workouts — adapting sessions to your fitness, heart rate and available time",
      "To process payments — billing your chosen plan and managing your subscription",
    ],
  },
  {
    title: "Third Parties We Work With",
    items: [
      "Stripe — payment processing",
      "RevenueCat — subscription management across app stores",
      "YMove — exercise content",
      "Resend — transactional email delivery",
    ],
  },
  {
    title: "Data Storage, Security & Your Rights",
    items: [
      "Your data is stored securely using industry-standard encryption in transit and at rest",
      "We never sell your data to third parties — ever",
      "You can request deletion of your data at any time by emailing info@fit50app.com",
    ],
  },
];

export const Privacy = () => (
  <div data-testid="privacy-page" className="min-h-screen bg-obsidian relative">
    <div className="absolute inset-0 grid-lines" />
    <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-14">
      <div className="flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <span className="grid place-items-center h-8 w-8 bg-volt text-white transition-transform duration-300 group-hover:rotate-12">
            <Zap className="h-4 w-4" strokeWidth={3} />
          </span>
          <span className="font-display font-black text-2xl tracking-tight uppercase">
            Fit<span className="text-volt">/</span>50
          </span>
        </Link>
        <Link
          data-testid="privacy-back-link"
          to="/"
          className="inline-flex items-center gap-2 font-mono text-[11px] tracking-widest uppercase text-neutral-500 hover:text-volt transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </Link>
      </div>

      <div className="mt-14">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-volt">Legal</p>
        <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mt-3">
          Privacy Policy
        </h1>
        <p className="mt-3 font-mono text-[11px] tracking-widest uppercase text-neutral-500">
          Fit50 • fit50app.com • Effective September 2026
        </p>
      </div>

      <div className="mt-10 space-y-4">
        {SECTIONS.map((s, i) => (
          <section key={s.title} data-testid={`privacy-section-${i + 1}`} className="border border-white/10 bg-carbon p-7">
            <h2 className="font-display font-extrabold uppercase text-xl sm:text-2xl">
              <span className="text-volt mr-3">{String(i + 1).padStart(2, "0")}</span>
              {s.title}
            </h2>
            <ul className="mt-4 space-y-2.5">
              {s.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-neutral-400 leading-relaxed">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 bg-volt" />
                  {item}
                </li>
              ))}
            </ul>
          </section>
        ))}

        <section data-testid="privacy-section-contact" className="border border-white/10 bg-carbon p-7">
          <h2 className="font-display font-extrabold uppercase text-xl sm:text-2xl">
            <span className="text-volt mr-3">05</span>
            Contact
          </h2>
          <p className="mt-4 text-sm sm:text-base text-neutral-400 leading-relaxed">
            Questions about your data? Email us at{" "}
            <a href="mailto:info@fit50app.com" className="text-volt hover:underline">info@fit50app.com</a>.
          </p>
        </section>

        <section data-testid="privacy-section-law" className="border border-white/10 bg-carbon p-7">
          <h2 className="font-display font-extrabold uppercase text-xl sm:text-2xl">
            <span className="text-volt mr-3">06</span>
            Governing Law
          </h2>
          <p className="mt-4 text-sm sm:text-base text-neutral-400 leading-relaxed">
            This policy is governed by the laws of New Zealand.
          </p>
        </section>
      </div>

      <p className="mt-10 font-mono text-[10px] tracking-widest uppercase text-neutral-600">
        © 2026 Fit50 Performance Inc. All rights reserved.
      </p>
    </div>
  </div>
);
